/**
 * CSV export — one implementation for the whole app.
 *
 * Harvest gives you a CSV on every report; so do we, and every one of ours has
 * to escape and encode identically. Two copies of this drifted apart once
 * already (one escaped `\r`, the other did not), which is how an export from
 * one screen opens cleanly in Excel and the next one doesn't.
 *
 * The button that calls this is `<ExportCsvButton>`, labelled **"Export CSV"**
 * everywhere — not "Export", not "CSV".
 *
 * Three rules every export on every screen follows:
 *
 * 1. **It exports what is on screen** — the current filters, the current
 *    search, the current sort, in the current order. A file that quietly
 *    contains rows the user filtered out is worse than no export, because
 *    nothing on screen contradicts it.
 * 2. **It is named for what it contains** — `csvFilename()` builds
 *    `entity-view-org-period.csv`, so three exports from three orgs in one
 *    Downloads folder are still tellable apart a week later.
 * 3. **Cells are data, not UI.** Money goes in as `1234.56`, never `$1,234.56`
 *    or `(1,234.56)`; a negative is an ASCII `-`, never the U+2212 the screen
 *    prints, because a spreadsheet cannot sum a column of typography. The
 *    PRECISION still follows the screen — `csvMoney(v, { whole: true })` on a
 *    screen that prints whole-dollar rollups (`/clients`), cents on a screen
 *    that prints cents (`/expenses`, `/invoices`) — so the file and the page
 *    never disagree about a figure. One precision per COLUMN, including its
 *    total row: a totals cell rounded differently from the rows above it turns
 *    `SUM()` into a rounding error the reader gets blamed for.
 */

import { selectActiveOrg, useAuthStore } from '../features/auth/store';

export type CsvCell = string | number | null | undefined;

function escapeCell(cell: CsvCell): string {
  const value = cell == null ? '' : String(cell);
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function downloadCsv(filename: string, rows: CsvCell[][]): void {
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\r\n');
  // BOM so Excel reads UTF-8 names correctly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Filenames
// ---------------------------------------------------------------------------

/** `Last 12 months` → `last-12-months`; empty stays empty. */
function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Local YYYY-MM-DD — the same calendar day the user is looking at. */
function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

export interface CsvPeriod {
  from?: string | null;
  to?: string | null;
}

export interface CsvNameParts {
  /** The noun, plural: `invoices`, `clients`, `expenses`, `support`. */
  entity: string;
  /** What the current filters narrowed it to: `overdue`, `archived`, `q-acme`. */
  view?: string | null;
  /**
   * What the figures cover: a month key (`2026-08`), a label (`Last 12
   * months`), or a `{from,to}` range. Omitted → the export date, because a
   * file with no period in its name is a file nobody can date later.
   */
  period?: string | CsvPeriod | null;
}

/** The active org's slug (`solid-digital`), so two tenants' files never collide. */
function orgSlug(): string {
  const state = useAuthStore.getState();
  const org = selectActiveOrg(state);
  return slug(org?.slug || org?.name || state.activeOrgId || '');
}

function periodSlug(period: CsvNameParts['period']): string {
  if (!period) return today();
  if (typeof period === 'string') return slug(period) || today();
  const { from, to } = period;
  if (from && to) return from === to ? from : `${from}-to-${to}`;
  if (from) return `from-${from}`;
  if (to) return `through-${to}`;
  return today();
}

/**
 * `invoices-overdue-solid-digital-2026-08-12.csv`
 *
 * entity · what the filters left · which org · what period. Every part that is
 * unknown drops out rather than leaving `--` in the name.
 */
export function csvFilename({ entity, view, period }: CsvNameParts): string {
  const parts = [slug(entity), view ? slug(view) : '', orgSlug(), periodSlug(period)];
  return `${parts.filter(Boolean).join('-')}.csv`;
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/**
 * Money as a number a spreadsheet can add: `1234.56`, or `1235` where the
 * screen shows a whole-dollar rollup. No symbol, no thousands separator, no
 * parentheses — the sign is an ASCII minus so `SUM()` works.
 */
export function csvMoney(value: number, options: { whole?: boolean } = {}): string {
  return options.whole ? String(Math.round(value)) : value.toFixed(2);
}

/** Hours as `21.50` — two decimals, matching `formatHours({ fixed: true })`. */
export function csvHours(value: number): string {
  return value.toFixed(2);
}

/**
 * A ratio as the percentage the screen prints, without the `%` sign (which
 * would make the cell text). `0.561` → `56.1`. Null → empty, never `0`: a
 * margin nobody can compute is not a margin of zero.
 */
export function csvPercent(value: number | null | undefined, decimals = 1): string {
  return value == null || Number.isNaN(value) ? '' : (value * 100).toFixed(decimals);
}
